import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BuildStatus, Currency, ExportDialogData } from '../../models';
import { ExportService } from '../../services/export.service';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export enum BasicDate {
  LAST_30_DAYS,
  LAST_60_DAYS,
  LAST_90_DAYS,
  LAST_MONTH,
  LAST_QUARTER,
  CUSTOM,
}

const DEFAULT_BASIC_DATE = BasicDate.LAST_30_DAYS;
const VENDASTA_PAYMENT_CURRENCIES = ['USD', 'CAD', 'AUD', 'GBP', 'NZD', 'CZK'];

interface BasicDateOption {
  label: string;
  value: BasicDate;
}

@Component({
  standalone: true,
  templateUrl: './export-dialog.component.html',
  styleUrls: ['./export-dialog.component.scss'],
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    FormsModule,
    TranslateModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatSelectModule,
    GalaxyAlertModule,
    GalaxyButtonLoadingIndicatorModule,
    GalaxyFormFieldModule,
    MatProgressSpinnerModule,
  ],
  providers: [ExportService],
})
export class ExportDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  basicDateOptions: BasicDateOption[];
  BasicDate = BasicDate;
  today = new Date();

  currencyOptions: Currency[];
  public downloading$: Observable<boolean> = this.exportService.buildState$.pipe(
    map((buildState) => buildState === BuildStatus.BUILDING),
  );
  public errorMessage$: Observable<string | null> = this.exportService.errorMessage$;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    public dialogRef: MatDialogRef<ExportDialogData>,
    @Inject(MAT_DIALOG_DATA) public data: ExportDialogData,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private exportService: ExportService,
  ) {}

  ngOnInit(): void {
    this.basicDateOptions = [
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.LAST_30_DAYS'),
        value: BasicDate.LAST_30_DAYS,
      },
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.LAST_60_DAYS'),
        value: BasicDate.LAST_60_DAYS,
      },
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.LAST_90_DAYS'),
        value: BasicDate.LAST_90_DAYS,
      },
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.LAST_MONTH'),
        value: BasicDate.LAST_MONTH,
      },
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.LAST_QUARTER'),
        value: BasicDate.LAST_QUARTER,
      },
      {
        label: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.DATE_RANGE_OPTIONS.CUSTOM'),
        value: BasicDate.CUSTOM,
      },
    ];

    const availableCurrencies = VENDASTA_PAYMENT_CURRENCIES.map((currency) => ({ id: currency, name: currency }));
    this.currencyOptions = [
      { id: '', name: this.translateService.instant('PAYMENTS_EXPORT.DIALOG.ALL_CURRENCIES') },
      ...availableCurrencies,
    ];

    this.form = this.fb.group({
      basicDate: [this.initialBasicDateValue(), Validators.required],
      startInterval: [this.data.startInterval, Validators.required],
      endInterval: [this.data.endInterval, Validators.required],
      currency: [''],
    });

    this.form
      .get('basicDate')
      .valueChanges.pipe(
        takeUntil(this.destroyed$),
        filter((basicDate) => basicDate !== BasicDate.CUSTOM),
      )
      .subscribe((basicDate) => {
        const { startInterval, endInterval } = intervalsFromBasicDate(basicDate);
        this.form.get('startInterval').setValue(startInterval);
        this.form.get('endInterval').setValue(endInterval);
      });

    this.downloading$.pipe(takeUntil(this.destroyed$)).subscribe((downloading) => {
      if (downloading) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get basicDate(): BasicDate {
    return this.form.get('basicDate').value;
  }

  handleSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const startInterval = new Date(this.form.get('startInterval').value).setUTCHours(0, 0, 0, 0);
    const endInterval = new Date(this.form.get('endInterval').value).setUTCHours(0, 0, 0, 0);

    this.exportService.export({
      merchantID: this.data?.merchantId,
      startInterval: new Date(startInterval),
      endInterval: new Date(endInterval),
      currency: this.form.get('currency').value,
    });
  }

  handleCancel(): void {
    this.exportService.cancel();
    this.dialogRef.close();
  }

  private initialBasicDateValue(): BasicDate {
    return !!this.data.startInterval || !!this.data.endInterval ? BasicDate.CUSTOM : DEFAULT_BASIC_DATE;
  }
}

export function intervalsFromBasicDate(basicDate: BasicDate): { startInterval: Date; endInterval: Date } {
  let startInterval: Date = null;
  let endInterval: Date = null;

  const today = new Date();

  switch (basicDate) {
    case BasicDate.LAST_30_DAYS:
      startInterval = new Date(new Date().setDate(new Date().getDate() - 30));
      endInterval = today;
      break;
    case BasicDate.LAST_60_DAYS:
      startInterval = new Date(new Date().setDate(new Date().getDate() - 60));
      endInterval = today;
      break;
    case BasicDate.LAST_90_DAYS:
      startInterval = new Date(new Date().setDate(new Date().getDate() - 90));
      endInterval = today;
      break;
    case BasicDate.LAST_MONTH:
      startInterval = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endInterval = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case BasicDate.LAST_QUARTER: {
      const quarter = Math.floor(today.getMonth() / 3);
      startInterval = new Date(today.getFullYear(), quarter * 3 - 3, 1);
      endInterval = new Date(startInterval.getFullYear(), startInterval.getMonth() + 3, 0);
      break;
    }
  }

  // Zero UTC time
  if (startInterval !== null) {
    startInterval.setUTCHours(0, 0, 0, 0);
  }
  if (endInterval !== null) {
    endInterval.setUTCHours(0, 0, 0, 0);
  }

  return { startInterval, endInterval };
}
